import DOMPurify from 'dompurify';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { DEFAULT_ZOOM, ZOOM_LEVELS } from '../constants';
import {
  defaultFilingInfo,
  filingPathToInfo,
  getFilingInfoFromPath,
} from '../data';
import { colors } from '../theme';
import type { SecViewerProps } from '../types';
import { clearHighlights, findAndHighlightInstance } from '../utils/highlight';
import { normalizeSecUrl, urlToFilingPath } from '../utils/sec-url';
import {
  clearXbrlHighlights,
  highlightByXbrlTag,
} from '../utils/xbrl-highlight';
import { FinancialDataPane } from './FinancialDataPane/index';

export function SecViewer({
  url,
  xbrlTag,
  periodEndDate,
  periodStartDate,
  searchText,
  instance,
  onFilingChange,
  embed = false,
}: SecViewerProps): React.ReactElement {
  const [html, setHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [highlightFound, setHighlightFound] = useState(false);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [showDataPane, setShowDataPane] = useState(false);
  const [currentFilingType, setCurrentFilingType] = useState<string>('');
  const [currentFilingYear, setCurrentFilingYear] = useState<string>('');
  const containerRef = useRef<HTMLDivElement>(null);

  // ── postMessage to parent when in embed mode ──
  useEffect(() => {
    if (!embed) return;
    if (!url) return;

    const filingPath = urlToFilingPath(url);
    if (!filingPath) return;

    const params = new URLSearchParams();
    params.set('f', filingPath);
    if (xbrlTag) params.set('tag', xbrlTag);
    if (periodEndDate) params.set('end', periodEndDate);
    if (periodStartDate) params.set('start', periodStartDate);
    if (searchText) params.set('s', searchText);
    if (instance != null) params.set('i', String(instance));
    const sourceUrl = `https://source.meridian.ai?${params.toString()}`;

    window.parent.postMessage({ type: 'meridian-db-url', url: sourceUrl }, '*');
  }, [
    embed,
    url,
    xbrlTag,
    periodEndDate,
    periodStartDate,
    searchText,
    instance,
  ]);

  // Handle source link click - navigate to the full source URL
  const handleSourceLinkClick = useCallback(
    (sourceUrl: string): void => {
      try {
        const parsedUrl = new URL(sourceUrl);
        const filingPath = parsedUrl.searchParams.get('f');
        const tag = parsedUrl.searchParams.get('tag');
        const endDate = parsedUrl.searchParams.get('end');
        const startDate = parsedUrl.searchParams.get('start');

        if (filingPath && onFilingChange) {
          onFilingChange(filingPath, tag, endDate, startDate);
        } else if (filingPath) {
          const newParams = new URLSearchParams();
          newParams.set('f', filingPath);
          if (tag) newParams.set('tag', tag);
          if (endDate) newParams.set('end', endDate);
          if (startDate) newParams.set('start', startDate);
          window.location.href = `?${newParams.toString()}`;
        }
      } catch {
        window.open(sourceUrl, '_blank');
      }
    },
    [onFilingChange]
  );

  // Fetch the SEC filing HTML
  useEffect(() => {
    async function fetchFiling(): Promise<void> {
      setLoading(true);
      setError(null);

      // Normalize URL to handle iXBRL viewer URLs
      const normalizedUrl = normalizeSecUrl(url);

      try {
        // Use backend proxy to avoid CORS issues
        const proxyUrl = `/api/sec-proxy?url=${encodeURIComponent(normalizedUrl)}`;
        const response = await fetch(proxyUrl);

        if (!response.ok) {
          throw new Error(`Failed to fetch filing: ${response.status}`);
        }

        const text = await response.text();
        setHtml(text);
      } catch {
        // Fallback: Try direct fetch (may work for some SEC URLs)
        try {
          const response = await fetch(normalizedUrl);
          if (!response.ok) {
            throw new Error(`Failed to fetch: ${response.status}`);
          }
          const text = await response.text();
          setHtml(text);
        } catch {
          setError(
            `Could not load filing. CORS may be blocking the request. ` +
              `Set up the backend proxy at /api/sec-proxy.`
          );
        }
      } finally {
        setLoading(false);
      }
    }

    fetchFiling();
  }, [url]);

  // Apply highlighting when HTML is loaded or highlight params change
  useEffect(() => {
    if (!html || !containerRef.current) return;

    const container = containerRef.current;
    container.innerHTML = DOMPurify.sanitize(html, {
      ADD_TAGS: [
        'ix:nonfraction',
        'ix:nonnumeric',
        'ix:fraction',
        'ix:header',
        'ix:hidden',
        'ix:references',
        'ix:resources',
        'ix:tuple',
        'ix:continuation',
        'ix:exclude',
        'xbrli:context',
        'xbrli:entity',
        'xbrli:identifier',
        'xbrli:period',
        'xbrli:startdate',
        'xbrli:enddate',
        'xbrli:instant',
        'xbrli:segment',
        'xbrli:scenario',
        'xbrli:unit',
        'xbrli:measure',
        'xbrli:divide',
        'xbrli:unitnumerator',
        'xbrli:unitdenominator',
        'xbrldi:explicitmember',
        'xbrldi:typedmember',
      ],
      ADD_ATTR: [
        'contextref',
        'unitref',
        'decimals',
        'scale',
        'format',
        'sign',
        'name',
        'scheme',
        'dimension',
      ],
    });

    if (xbrlTag && periodEndDate) {
      const found = highlightByXbrlTag(
        container,
        xbrlTag,
        periodEndDate,
        periodStartDate
      );
      setHighlightFound(found);
    } else if (searchText) {
      const found = findAndHighlightInstance(
        container,
        searchText,
        instance ?? 1
      );
      setHighlightFound(found);
    } else {
      clearXbrlHighlights(container);
      clearHighlights(container);
      setHighlightFound(false);
    }
  }, [html, xbrlTag, periodEndDate, periodStartDate, searchText, instance]);

  // Re-scroll to highlighted element when zoom changes
  useEffect(() => {
    if (!containerRef.current) return;
    const activeHighlight =
      containerRef.current.querySelector('.xbrl-highlight') ??
      containerRef.current.querySelector('.sec-highlight-active');
    if (activeHighlight) {
      setTimeout(() => {
        activeHighlight.scrollIntoView({
          behavior: 'instant',
          block: 'center',
        });
      }, 50);
    }
  }, [zoom]);

  // Extract filing info from URL for display
  // First try to match from our known filing paths (most accurate)
  const pathInfo = getFilingInfoFromPath(url);

  // Fallback: extract ticker from filename pattern
  const filingInfoMatch = url.match(/\/([a-z]{2,5})-(\d{8})\.(htm|html)/i);
  const ticker = pathInfo?.ticker || filingInfoMatch?.[1]?.toUpperCase() || '';

  // Filing info comes from path mapping (most accurate), then FinancialDataPane state, then default lookup
  const defaultInfo = defaultFilingInfo[ticker.toUpperCase()];
  const filingType =
    pathInfo?.filingType || currentFilingType || defaultInfo?.filingType || '';
  const filingYear =
    pathInfo?.year || currentFilingYear || defaultInfo?.year || '';

  if (error) {
    return (
      <div
        className='flex items-center justify-center min-h-screen bg-surface'
        style={{ fontFamily: 'Geist, Aptos, system-ui, sans-serif' }}
      >
        <div
          className='text-center max-w-md bg-white p-8 rounded-lg border border-edge'
          style={{ boxShadow: '0 4px 12px rgba(15, 41, 71, 0.08)' }}
        >
          <div className='w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 bg-red-50'>
            <svg
              className='w-6 h-6'
              fill='none'
              stroke='#dc2626'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
              />
            </svg>
          </div>
          <h2 className='text-ink text-lg font-semibold mb-2'>
            Failed to Load Filing
          </h2>
          <p className='text-ink-secondary text-sm mb-4 leading-normal'>
            {error}
          </p>
          <p className='text-ink-muted text-xs break-all'>{url}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className='h-screen flex flex-col overflow-hidden'
      style={{ fontFamily: 'Geist, Aptos, system-ui, sans-serif' }}
    >
      {/* Header bar — hidden in embed mode (when loaded in iframe from meridian-db) */}
      {!embed && (
        <header className='shrink-0 flex items-center justify-between px-5 h-14 bg-navy border-b border-navy-mid'>
          <div className='flex items-center gap-4'>
            {/* Meridian wordmark */}
            <span className='text-white text-base font-semibold tracking-[-0.02em]'>
              Meridian
            </span>
            <span className='text-white/30 text-base'>|</span>

            {ticker && (
              <div className='px-2.5 py-1 rounded bg-white/10 text-white text-[13px] font-medium'>
                {ticker}
                {filingType && ` ${filingType}`}
                {filingYear && ` ${filingYear}`}
              </div>
            )}
          </div>

          <div className='flex items-center gap-3'>
            {/* Data pane toggle button */}
            <button
              onClick={() => setShowDataPane(prev => !prev)}
              className='flex items-center gap-2 px-3 py-1.5 rounded transition-all border-none text-white text-[13px] font-medium'
              style={{
                background: showDataPane
                  ? 'rgba(255,255,255,0.15)'
                  : 'rgba(255,255,255,0.08)',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = showDataPane
                  ? 'rgba(255,255,255,0.15)'
                  : 'rgba(255,255,255,0.08)';
              }}
            >
              <svg
                className='w-4 h-4'
                fill='none'
                stroke='currentColor'
                strokeWidth={1.5}
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 01-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0112 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M10.875 12h2.25m-2.25 0c-.621 0-1.125.504-1.125 1.125M13.125 12c.621 0 1.125.504 1.125 1.125m-2.25 0v1.5m0-1.5c0 .621-.504 1.125-1.125 1.125m2.25-1.125c0 .621.504 1.125 1.125 1.125m-2.25 0h2.25m0 0v1.5'
                />
              </svg>
              MER DB
            </button>

            {/* Zoom controls - unified button group */}
            <div className='flex items-center bg-white/8 rounded-md p-0.5'>
              <button
                onClick={() =>
                  setZoom(
                    z => ZOOM_LEVELS[Math.max(0, ZOOM_LEVELS.indexOf(z) - 1)]
                  )
                }
                disabled={zoom <= ZOOM_LEVELS[0]}
                className='flex items-center justify-center transition-all w-7 h-7 rounded'
                style={{
                  background: 'transparent',
                  color:
                    zoom <= ZOOM_LEVELS[0]
                      ? 'rgba(255,255,255,0.25)'
                      : 'rgba(255,255,255,0.8)',
                  cursor: zoom <= ZOOM_LEVELS[0] ? 'not-allowed' : 'pointer',
                }}
                onMouseEnter={e => {
                  if (zoom > ZOOM_LEVELS[0])
                    e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <svg
                  className='w-3.5 h-3.5'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth={2.5}
                  viewBox='0 0 24 24'
                >
                  <path strokeLinecap='round' d='M19 12H5' />
                </svg>
              </button>
              <div className='flex items-center justify-center min-w-[52px] h-7 px-1 text-white text-xs font-medium tracking-[-0.01em]'>
                {zoom}%
              </div>
              <button
                onClick={() =>
                  setZoom(
                    z =>
                      ZOOM_LEVELS[
                        Math.min(
                          ZOOM_LEVELS.length - 1,
                          ZOOM_LEVELS.indexOf(z) + 1
                        )
                      ]
                  )
                }
                disabled={zoom >= ZOOM_LEVELS[ZOOM_LEVELS.length - 1]}
                className='flex items-center justify-center transition-all w-7 h-7 rounded'
                style={{
                  background: 'transparent',
                  color:
                    zoom >= ZOOM_LEVELS[ZOOM_LEVELS.length - 1]
                      ? 'rgba(255,255,255,0.25)'
                      : 'rgba(255,255,255,0.8)',
                  cursor:
                    zoom >= ZOOM_LEVELS[ZOOM_LEVELS.length - 1]
                      ? 'not-allowed'
                      : 'pointer',
                }}
                onMouseEnter={e => {
                  if (zoom < ZOOM_LEVELS[ZOOM_LEVELS.length - 1])
                    e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <svg
                  className='w-3.5 h-3.5'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth={2.5}
                  viewBox='0 0 24 24'
                >
                  <path strokeLinecap='round' d='M12 5v14m7-7H5' />
                </svg>
              </button>
            </div>
          </div>
        </header>
      )}

      {/* Highlight info bar */}
      {(xbrlTag || searchText) && (
        <div className='shrink-0 flex items-center px-5 h-12 bg-surface-alt border-b border-edge'>
          <div className='flex items-center gap-4 min-w-0'>
            {xbrlTag ? (
              <>
                <span
                  className='px-2.5 py-1 rounded truncate text-navy text-[13px] font-semibold min-w-0 max-w-[400px]'
                  title={xbrlTag}
                  style={{
                    background: `${colors.highlightActive}15`,
                    border: `1px solid ${colors.highlightActive}30`,
                  }}
                >
                  {xbrlTag}
                </span>
                {periodEndDate && (
                  <span className='px-2 py-1 rounded bg-surface text-ink-secondary text-[13px] border border-edge'>
                    {periodEndDate}
                  </span>
                )}
              </>
            ) : (
              <>
                <span className='text-ink-muted text-[13px]'>Search:</span>
                <span
                  className='px-2.5 py-1 rounded text-navy text-[13px] font-semibold'
                  style={{
                    background: `${colors.highlightActive}15`,
                    border: `1px solid ${colors.highlightActive}30`,
                  }}
                >
                  {searchText}
                </span>
              </>
            )}
            {!loading && !highlightFound && (
              <span className='text-ink-muted text-[13px]'>
                Not found in filing
              </span>
            )}
          </div>
        </div>
      )}

      {/* Main content area with optional data pane */}
      <div className='flex-1 min-h-0 flex overflow-hidden'>
        {/* Financial Data Pane */}
        {showDataPane && (
          <FinancialDataPane
            onSourceLinkClick={handleSourceLinkClick}
            onClose={() => setShowDataPane(false)}
            currentXbrlTag={xbrlTag}
            ticker={ticker}
            onFilingTypeChange={setCurrentFilingType}
            onFilingYearChange={setCurrentFilingYear}
          />
        )}

        {/* Filing content - scrollable wrapper */}
        <div className='flex-1 min-h-0 overflow-auto bg-white'>
          {/* Scaled content - always rendered, content updates when HTML loads */}
          <div
            ref={containerRef}
            style={{
              padding: '32px 48px',
              isolation: 'isolate',
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top left',
              width: `${10000 / zoom}%`,
              opacity: loading && !html ? 0.5 : 1,
              transition: 'opacity 0.15s ease',
            }}
          />
        </div>
      </div>

      {/* Highlight styles */}
      <style>{`
        .xbrl-highlight {
          background-color: #FFFF00 !important;
          border-radius: 3px;
          outline: 1px solid #eab308;
          scroll-margin-top: 120px;
        }
        .sec-highlight {
          background-color: #fef08a !important;
          border-radius: 2px;
        }
        .sec-highlight-active {
          background-color: #FFFF00 !important;
          outline: 1px solid #eab308;
          scroll-margin-top: 120px;
        }
      `}</style>
    </div>
  );
}
